/**
 * In-memory store of workflow runs, keyed by run id.
 *
 * The store accepts parsed CI events and reconciles them onto a per-run
 * `RunState`, then notifies subscribers. It is deliberately framework-free and
 * holds no DOM/relay references so it can be tested in isolation.
 */
import type {
  CiEvent,
  Job,
  JobResult,
  WorkflowProgress,
  WorkflowResult,
  Conclusion,
} from './types';
import { deriveJobStatus, shortSha } from './lib/parse';

/** Aggregated state for a single workflow run. */
export interface RunState {
  runId: string;
  commit: string;
  shortCommit: string;
  status: 'queued' | 'in_progress' | 'concluded';
  conclusion?: Conclusion;
  trigger?: WorkflowProgress['trigger'];
  /** Raw NIP-34 repo address (`a` tag), '' when unknown. */
  repoAddr: string;
  /** Repo id (third colon-segment of the `a` tag), '' when unknown. */
  repoId: string;
  /** Short repo owner (first 8 chars of the owner pubkey), '' when unknown. */
  repoOwner: string;
  /** Raw git ref (`r` tag, e.g. `refs/heads/main`), '' when unknown. */
  ref: string;
  /** Display branch (`refs/heads/` stripped), '' when unknown. */
  branch: string;
  /** Jobs keyed by job id; present once a job starts or completes. */
  jobs: Map<string, Job>;
  /** The in-progress job ids reported by the latest progress event. */
  inProgressJobs: string[];
  /** When the run's latest event was seen (epoch seconds). */
  updatedAt: number;
  createdAt: number;
}

export type StoreListener = (runs: ReadonlyMap<string, RunState>) => void;

/**
 * Repo/branch labels for a run. Every CI event carries the repo address (`a`)
 * and the git ref (`r`), but an event may omit them (or a Job Result may be
 * the only event seen for a run), so the newest non-empty value wins and the
 * previously known label is kept as the fallback. Always yields strings —
 * '' means "unknown" and the UI renders that explicitly.
 */
function repoBranchFields(
  ev: { repoAddr: string; repoId: string; repoOwner: string; ref: string; branch: string },
  existing?: RunState,
): Pick<RunState, 'repoAddr' | 'repoId' | 'repoOwner' | 'ref' | 'branch'> {
  return {
    repoAddr: ev.repoAddr || existing?.repoAddr || '',
    repoId: ev.repoId || existing?.repoId || '',
    repoOwner: ev.repoOwner || existing?.repoOwner || '',
    ref: ev.ref || existing?.ref || '',
    branch: ev.branch || existing?.branch || '',
  };
}

/** Sort runs newest-updated first for display. */
export function sortRuns(runs: Iterable<RunState>): RunState[] {
  return [...runs].sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * In-memory run/job store with subscribe-notify and update/reconcile.
 * `Date.now` and pruning are injectable for tests via the constructor options.
 */
export class CiStore {
  private runs = new Map<string, RunState>();
  private listeners = new Set<StoreListener>();
  private now: () => number;
  private retentionMs: number;
  /** Event ids already ingested, to dedup regular-kind events (9841/9842). */
  private seenEventIds = new Set<string>();
  /**
   * created_at of the newest Workflow Progress (39842) applied per run id.
   * 39842 is addressable (NIP-C1): the same run can be republished, and with
   * two relay filters an older revision can be delivered AFTER a newer one.
   * Latest-created_at-wins (NIP-01 replaceable-event semantics) means such a
   * stale event must be dropped rather than regress the newer run state.
   */
  private progressCreatedAt = new Map<string, number>();

  constructor(opts?: { now?: () => number; retentionMs?: number }) {
    this.now = opts?.now ?? (() => Date.now());
    this.retentionMs = opts?.retentionMs ?? 30 * 60 * 1000;
  }

  /** Return a live read-only view of the runs map. */
  snapshot(): ReadonlyMap<string, RunState> {
    return this.runs;
  }

  /** Subscribe to run-state changes. Returns an unsubscribe function. */
  subscribe(listener: StoreListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Ingest a single parsed CI event, routing by kind into the run/job map,
   * then fire notify so render.ts repaints.
   *
   * Dedup semantics (NIP-C1): 39842 is addressable (same `d` replaces prior
   * progress for that run), so it is NOT deduped by id — instead the
   * replacement is latest-`created_at`-wins, so a stale revision cannot
   * regress newer run state. 9841/9842 are regular kinds and are APPENDED but
   * guarded against duplicate event ids.
   */
  ingest(event: CiEvent): void {
    if (event.kind !== 39842) {
      if (this.seenEventIds.has(event.id)) return;
      this.seenEventIds.add(event.id);
    } else if (this.isStaleProgress(event)) {
      // Addressable replacement: keep the newest revision of the run.
      return;
    }
    switch (event.kind) {
      case 39842:
        this.reconcileProgress(event);
        break;
      case 9842:
        this.reconcileWorkflowResult(event);
        break;
      case 9841:
        this.reconcileJobResult(event);
        break;
    }
    this.emit();
  }

  /** Prune runs whose latest update is older than the retention window. */
  prune(): number {
    const cutoff = this.now() - this.retentionMs;
    let removed = 0;
    for (const [id, run] of this.runs) {
      if (run.updatedAt * 1000 < cutoff) {
        this.runs.delete(id);
        this.progressCreatedAt.delete(id);
        removed++;
      }
    }
    if (removed > 0) this.emit();
    return removed;
  }

  /**
   * True when a 39842 revision is older than the one already applied for that
   * run, i.e. a stale re-delivery that must not overwrite newer state.
   */
  private isStaleProgress(ev: WorkflowProgress): boolean {
    const appliedAt = this.progressCreatedAt.get(ev.runId);
    return appliedAt !== undefined && ev.createdAt < appliedAt;
  }

  private reconcileProgress(ev: WorkflowProgress): void {
    this.progressCreatedAt.set(ev.runId, ev.createdAt);
    const existing = this.runs.get(ev.runId);
    const jobs = new Map(existing?.jobs ?? []);
    // Completed job refs become concluded jobs; in-progress jobs stay in_progress.
    // Progress events reference jobs by workflow-declared job id (no event id
    // yet), so these are keyed by job id as a placeholder and reconciled to the
    // Job Result event id when the 9841 arrives.
    for (const jobId of ev.completedJobRefs) {
      const prior = jobs.get(jobId);
      jobs.set(jobId, {
        id: jobId,
        jobId,
        name: prior?.name,
        status: 'concluded',
        conclusion: prior?.conclusion ?? ev.conclusion,
      });
    }
    for (const jobId of ev.inProgressJobs) {
      const prior = jobs.get(jobId);
      jobs.set(jobId, {
        id: jobId,
        jobId,
        name: prior?.name,
        status: 'in_progress',
        conclusion: prior?.conclusion,
      });
    }
    this.runs.set(ev.runId, {
      runId: ev.runId,
      commit: ev.commit,
      shortCommit: shortSha(ev.commit),
      status: ev.status,
      conclusion: ev.conclusion ?? existing?.conclusion,
      trigger: ev.trigger ?? existing?.trigger,
      ...repoBranchFields(ev, existing),
      jobs,
      inProgressJobs: ev.inProgressJobs,
      updatedAt: ev.createdAt,
      createdAt: existing?.createdAt ?? ev.createdAt,
    });
  }

  private reconcileWorkflowResult(ev: WorkflowResult): void {
    const existing = this.runs.get(ev.runId);
    const jobs = existing?.jobs ? new Map(existing.jobs) : new Map();
    // Seed jobs from the workflow result's quoted job refs. Each quoted Job
    // Result event id becomes one per-instance job row (go-test runs 9x as
    // go-test-1..9, so 9 distinct rows).
    for (const ref of ev.jobRefs) {
      const prior = jobs.get(ref.eventId);
      jobs.set(ref.eventId, {
        id: ref.eventId,
        jobId: ref.jobId,
        name: prior?.name,
        status: 'concluded',
        conclusion: prior?.conclusion ?? ev.conclusion,
      });
    }
    // A workflow result finalizes the run; any known jobs are concluded.
    for (const job of jobs.values()) {
      job.status = 'concluded';
      job.conclusion = job.conclusion ?? ev.conclusion;
    }
    this.runs.set(ev.runId, {
      runId: ev.runId,
      commit: ev.commit,
      shortCommit: shortSha(ev.commit),
      status: 'concluded',
      conclusion: ev.conclusion,
      trigger: ev.trigger ?? existing?.trigger,
      ...repoBranchFields(ev, existing),
      jobs,
      inProgressJobs: [],
      updatedAt: ev.createdAt,
      createdAt: existing?.createdAt ?? ev.createdAt,
    });
  }

  private reconcileJobResult(ev: JobResult): void {
    const existing = this.runs.get(ev.runRef);
    const jobs = new Map(existing?.jobs ?? []);
    const prior = jobs.get(ev.id);
    const status = deriveJobStatus(ev.conclusion);
    // Key the job by its Job Result event id (per-instance). If a progress
    // event already seeded a placeholder under the workflow job id, remove it
    // so the run shows one row per instance, not a duplicate.
    if (ev.jobId && jobs.has(ev.jobId) && !jobs.has(ev.id)) {
      jobs.delete(ev.jobId);
    }
    jobs.set(ev.id, {
      id: ev.id,
      jobId: ev.jobId ?? prior?.jobId,
      name: ev.name ?? prior?.name,
      status,
      conclusion: ev.conclusion,
      exitCode: ev.exitCode ?? prior?.exitCode,
      logUrl: ev.logsUrl ?? prior?.logUrl,
      runsOn: ev.runsOn ?? prior?.runsOn,
      queuedAt: ev.queuedAt ?? prior?.queuedAt,
      startedAt: ev.startedAt ?? prior?.startedAt,
      logTail: ev.logTail ?? prior?.logTail,
    });
    const nowSec = Math.floor(this.now() / 1000);
    this.runs.set(ev.runRef, {
      runId: ev.runRef,
      commit: existing?.commit ?? '',
      shortCommit: shortSha(existing?.commit ?? ''),
      status: existing?.status ?? 'in_progress',
      conclusion: existing?.conclusion,
      trigger: existing?.trigger,
      ...repoBranchFields(ev, existing),
      jobs,
      inProgressJobs: existing?.inProgressJobs ?? [],
      updatedAt: Math.max(nowSec, ev.createdAt),
      createdAt: existing?.createdAt ?? nowSec,
    });
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener(this.runs);
    }
  }
}

/** Map a run's conclusion + status to a display conclusion (for the UI). */
export function runDisplayConclusion(run: RunState): Conclusion | undefined {
  return run.conclusion;
}
